import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { CardBackProps } from '../types';

import type { SubstantivoPayload } from './types';

/**
 * The answer, the plural, and the glosses in the learner's own languages.
 *
 * Chrome is English like every other deck; what is localised is the CONTENT,
 * which appears in the learner's own languages below. One deck speaking German
 * while the rest of the app speaks English is worse than either choice made
 * consistently — a UI language setting is its own piece of work.
 *
 * A reviewing model pointed out the trap that actually catches a German
 * speaker: the German word often has a DIFFERENT gender. `o sol` against `die
 * Sonne`, `a mesa` against `der Tisch`, `o medo` against `die Angst`. Her
 * instinct is not absent for those, it is present and pointing the wrong way,
 * which is harder to unlearn than having none.
 *
 * That was nearly a warning box, until the seed reported it fires on 57 of 115
 * cards. A warning on half the deck is not a warning, and it would have trained
 * her to skip the ending one that fires six times and matters. So the clash is
 * shown rather than announced: the German article is tinted by ITS gender with
 * the same colours the Portuguese one uses, and `o sol` in blue above `die
 * Sonne` in red says it without a sentence.
 *
 * The irregular-gender warning is the reason this app has its own back rather
 * than borrowing the noun one. `o problema` and `a mão` are where a German
 * speaker's instincts fail twice over: German trained her that gender must be
 * memorised word by word, Portuguese has just told her it is mostly readable
 * off the ending, and then these break the very rule that was the relief.
 * Saying so on the card is cheaper than letting it be discovered.
 */
/**
 * A German gloss with its article tinted by German gender.
 *
 * Same colour tokens as the Portuguese article above it, so a mismatch is
 * visible at a glance and needs no explaining. Glosses with no article — `zu
 * Hause`, `Sie (höflich, m.)` — render unchanged.
 */
function TintedGloss({ gloss }: { gloss: string }) {
  const match = gloss.match(/^(der|die|das)\s+(.*)$/i);
  if (!match) return <>{gloss}</>;
  const [, art, rest] = match;
  const tint =
    art.toLowerCase() === 'der'
      ? 'text-masculine'
      : art.toLowerCase() === 'die'
        ? 'text-feminine'
        : 'text-neutral-gender';
  return (
    <>
      <span className={tint}>{art}</span> {rest}
    </>
  );
}

export function CardBack({
  card,
  correct,
  user_answer,
  primary_lang,
  secondary_lang,
  isLastCard,
  onNext,
}: CardBackProps<SubstantivoPayload>) {
  const { article, plural, plural_note, irregular_gender } = card.payload;
  const tint = article === 'o' ? 'text-masculine' : 'text-feminine';

  const gloss = (lang: string): string[] => card.translations[lang] ?? [];
  const primary = gloss(primary_lang);
  const secondary = gloss(secondary_lang);

  return (
    <div className="border-border bg-card flex flex-col gap-6 rounded-2xl border p-6 shadow-sm sm:p-8">
      <div className="flex items-center gap-3">
        {correct ? (
          <CheckCircle2 className="text-success size-6" aria-hidden />
        ) : (
          <XCircle className="text-destructive size-6" aria-hidden />
        )}
        <span className={cn('font-medium', correct ? 'text-success' : 'text-destructive')}>
          {correct ? 'Correct' : user_answer === null ? 'Skipped' : 'Not quite'}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <p className="font-display text-4xl">
          <span className={tint}>{article}</span> {card.de}
        </p>
        {plural ? (
          <p className="text-muted-foreground text-sm">
            Plural: <span className="text-foreground">{plural}</span>
            {plural_note && <span className="text-muted-foreground"> — {plural_note.en}</span>}
          </p>
        ) : (
          <p className="text-muted-foreground text-sm italic">no plural in everyday use</p>
        )}
      </div>

      {irregular_gender && (
        <div className="border-destructive/30 bg-destructive/5 flex gap-3 rounded-lg border p-3">
          <AlertTriangle className="text-destructive mt-0.5 size-4 shrink-0" aria-hidden />
          <p className="text-sm leading-relaxed">
            The ending misleads here — <span className="font-medium">{card.de}</span> is{' '}
            {article === 'o' ? 'masculine' : 'feminine'}.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-1">
        {primary.length > 0 && (
          <p className="text-lg">
            {primary.map((gloss, i) => (
              <span key={gloss}>
                {i > 0 && ', '}
                <TintedGloss gloss={gloss} />
              </span>
            ))}
          </p>
        )}
        {secondary.length > 0 && (
          <p className="text-muted-foreground text-sm">{secondary.join(', ')}</p>
        )}
      </div>

      <button
        type="button"
        onClick={onNext}
        className="bg-primary text-primary-foreground focus-visible:ring-ring min-h-12 w-full rounded-lg px-4 font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        {isLastCard ? 'Finish' : 'Next'}
      </button>
    </div>
  );
}
